import type { CopilotProposalSummary } from "@repo/app-types/entities/copilotProposal";
import type { CopilotProposalCardContent } from "../../copilot/copilotProposalCard.js";
import {
  buildSignalEditorPathname,
  buildSignalProposalEditLocation,
} from "./signalEditorUrlState.js";

/** What a signal proposal's card in the chat shows and opens. */
export function readSignalProposalCardContent(
  proposal: CopilotProposalSummary<"signal">,
): CopilotProposalCardContent {
  return {
    resourceType: "signal",
    operation: proposal.kind === "create_signal" ? "create" : "update",
    summary: proposal.data.summary,
    highlights: buildSignalProposalHighlights(proposal),
    review: {
      kind: "open_editor",
      location: buildSignalProposalEditLocation({
        signalId:
          proposal.kind === "create_signal" ? undefined : proposal.resource.id,
        proposalId: proposal.id,
      }),
    },
    resourceLocation: {
      pathname: buildSignalEditorPathname(proposal.resource.id),
    },
  };
}

/**
 * The card's highlights: the metadata a new signal extracts, or how much of an
 * existing signal's schema an update moves. The schema is the part of a signal
 * with consequences past detection — existing rows, filters and reports read
 * it — so its size is what the card reports.
 */
function buildSignalProposalHighlights(
  proposal: CopilotProposalSummary<"signal">,
): readonly string[] {
  const { data } = proposal;
  switch (data.kind) {
    case "create_signal":
      return data.metadata_schema.fields.length > 0
        ? [describeFieldCount(data.metadata_schema.fields.length)]
        : [];
    case "update_signal":
      if (!data.metadata_schema) {
        return [];
      }
      return proposal.changed_metadata_field_count !== undefined
        ? [
            `${describeFieldCount(proposal.changed_metadata_field_count)} changed`,
          ]
        : ["Metadata changed"];
  }
}

function describeFieldCount(count: number): string {
  return `${count} metadata field${count === 1 ? "" : "s"}`;
}
